import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { MazePatrolInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * MAZE PATROL — fifth cartridge. Pac-Man + Lady Bug heritage maze chase.
 *
 * 480x270 landscape with a centered 270x270 maze and HUD gutters per
 * the cabinet's landscape-only rule. Original mechanic: the whole
 * 9x9 maze rotates 90° every 60 seconds. Spawn cells + walls are
 * 4-way rotationally symmetric so the rotation never leaves anyone
 * embedded in a wall.
 *
 * MVP scope (sc-3127):
 *   - 9x9 grid w/ hand-authored 4-way symmetric maze
 *   - 4 thieves with distinct AIs (BFS pursuer + BFS ambusher +
 *     waypoint patroller + random wanderer)
 *   - 4 alarm panels (power pellets) → 8-second tag-the-thieves window
 *   - rotation event every 60s w/ 1.5s telegraph + 0.3s flash
 *   - 3 lives, 1.25s respawn invuln
 *   - level progression: collect all badges → +1000*level bonus + next level
 */
const module_: GameModule = {
  id: 'maze-patrol',
  title: 'MAZE PATROL',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'maze',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'ARROWS', action: 'MOVE' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Badge pickup — quick rising blip on every dot. Kept very short
    // so chained pickups don't blur into a wall of treble.
    ctx.registerSfx('badge', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 880 }, { t: 30, f: 1100 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.08 }, { t: 30, g: 0.0 }],
    });
    // Alarm panel triggered — pink-light moment. Longer, brighter.
    ctx.registerSfx('alarm-on', {
      wave: 'square',
      pitch: [{ t: 0, f: 300 }, { t: 200, f: 600 }, { t: 400, f: 300 }],
      gain: [{ t: 0, g: 0 }, { t: 10, g: 0.32 }, { t: 400, g: 0.0 }],
      fm: { ratio: 0.5, index: 25 },
    });
    // Thief tagged — high triumphant chime.
    ctx.registerSfx('thief-tag', {
      wave: 'square',
      pitch: [
        { t: 0, f: 660 },
        { t: 70, f: 990 },
        { t: 140, f: 1320 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 5, g: 0.28 }, { t: 140, g: 0.0 }],
    });
    // Player death — heavy descending crash.
    ctx.registerSfx('death', {
      wave: 'noise',
      pitch: [{ t: 0, f: 220 }, { t: 280, f: 60 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.55 }, { t: 280, g: 0.0 }],
    });
    // Rotation warning — low warble during the 1.5s telegraph.
    ctx.registerSfx('rotate-warn', {
      wave: 'sawtooth',
      pitch: [{ t: 0, f: 220 }, { t: 120, f: 160 }, { t: 240, f: 220 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.18 }, { t: 240, g: 0.0 }],
    });
    // Rotation fires — quick "whoosh" descending sweep.
    ctx.registerSfx('rotate', {
      wave: 'sawtooth',
      pitch: [{ t: 0, f: 1200 }, { t: 200, f: 200 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.3 }, { t: 200, g: 0.0 }],
      fm: { ratio: 0.5, index: 30 },
    });
    // Level cleared — ascending fanfare (cabinet-wide convention).
    ctx.registerSfx('level-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 100, f: 660 },
        { t: 200, f: 880 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 200, g: 0.0 }],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new MazePatrolInstance(ctx);
  },
};

export default module_;
